/**
 * In-chat image-generation widget (C8): appears the moment a `generate_image`
 * call settles with a queued job (or is re-created from a restored
 * transcript), tracks the C7 record to a terminal state, and flips to a
 * thumbnail + Download button when the image is delivered.
 *
 * Cost posture (deliberate): one status fetch on mount (so a restored card
 * showing a finished job resolves immediately), then a minute poll — ONLY
 * while the card is mounted, the tab is visible, and the job is still
 * non-terminal — plus a catch-up fetch when the tab returns to visible
 * (background tabs throttle timers, which used to strand the card in
 * "Generating…" long after delivery). A terminal state stops polling
 * permanently, and the thumbnail's presigned URL is fetched exactly once; the
 * Download button mints a fresh link per click so it can never hand out an
 * expired one.
 *
 * Restored cards from LEGACY transcript lines carry no jobId (the structured
 * `imageJobs` field postdates them). Those resolve their record once, by
 * nearest `createdAt` to the turn's timestamp; no match → a quiet
 * "no longer available" state (records fall off the capped listing).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { recordClientFailure } from '../../telemetry/rum.ts';
import { failureKind } from '../../telemetry/failureKind.ts';
import { listImageJobs, type ImageJob } from '../../agents/imageJobsApi.ts';
import { getDownloadUrl } from '../../agents/userFilesApi.ts';
import './ImageJobCard.css';

/** Poll cadence while non-terminal. Slow on purpose — the Jobs panel offers
 *  a faster view; this card just needs to catch the flip eventually. */
export const IMAGE_CARD_POLL_MS = 60_000;

/** Successful polls with this card's job absent from the listing before it is
 *  reported. Three at the 60s cadence ≈ 3 minutes of grace, which is longer
 *  than any legitimate gap between enqueue and the record appearing. */
const MISSING_POLLS_BEFORE_REPORT = 3;

/** How long a card may sit `generating` with its record present-but-unsettled
 *  before that is reported. Generation runs ~10 minutes and they queue, so this
 *  is deliberately generous: past it, nothing flipped the record at all. */
const STUCK_AFTER_MS = 30 * 60_000;

/** How far a record's `createdAt` may sit from a legacy turn's timestamp and
 *  still be that turn's image (covers a long tool loop + clock skew). */
const LEGACY_MATCH_WINDOW_MS = 30 * 60_000;

/** True when the temporary image's auto-delete instant has passed. */
function isExpired(expiresAt: string | undefined): boolean {
  if (expiresAt === undefined) {
    return false;
  }
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) && at <= Date.now();
}

/** Countdown window derived from the record's server-stamped ETA. */
interface EtaWindow {
  /** Epoch ms the job should complete (record `estimatedReadyAt`). */
  readonly readyAtMs: number;
  /** Full estimate span (enqueue → readyAt) in ms, for the progress bar. */
  readonly totalMs: number;
}

/** The record's countdown window, or null when it carries no usable ETA. */
function etaWindowOf(record: ImageJob): EtaWindow | null {
  const readyAtMs = record.estimatedReadyAt !== undefined ? Date.parse(record.estimatedReadyAt) : NaN;
  const createdMs = record.createdAt !== undefined ? Date.parse(record.createdAt) : NaN;
  if (!Number.isFinite(readyAtMs) || !Number.isFinite(createdMs) || readyAtMs <= createdMs) {
    return null;
  }
  return { readyAtMs, totalMs: readyAtMs - createdMs };
}

/** "2m 15s" / "45s" — compact remaining-time label. */
function formatRemaining(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  if (s < 60) {
    return `${s}s`;
  }
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/** "Temporary — expires in Nd; ask me to keep it." (floor 1 day). */
function expiryLine(expiresAt: string | undefined): string {
  if (expiresAt === undefined) {
    return 'Temporary — ask me to keep it permanently.';
  }
  const days = Math.max(1, Math.ceil((Date.parse(expiresAt) - Date.now()) / 86_400_000));
  return `Temporary — expires in ${days} day${days === 1 ? '' : 's'}; ask me to keep it.`;
}

/** The record nearest `turnTs` within the legacy window, or undefined. */
function nearestRecord(
  jobs: readonly ImageJob[],
  turnTs: string,
): ImageJob | undefined {
  const anchor = Date.parse(turnTs);
  if (!Number.isFinite(anchor)) {
    return undefined;
  }
  let best: ImageJob | undefined;
  let bestDistance = LEGACY_MATCH_WINDOW_MS;
  for (const job of jobs) {
    const created = job.createdAt !== undefined ? Date.parse(job.createdAt) : NaN;
    if (!Number.isFinite(created)) {
      continue;
    }
    const distance = Math.abs(created - anchor);
    if (distance <= bestDistance) {
      best = job;
      bestDistance = distance;
    }
  }
  return best;
}

interface ImageJobCardProps {
  /** Studio job id. Absent only on legacy restored cards, which resolve their
   *  record by time proximity to `turnTs` instead. */
  jobId?: string;
  /** The user's prompt (shown as the card title). */
  prompt: string;
  /** The assistant turn's timestamp — the legacy match anchor. */
  turnTs?: string;
}

type Phase =
  | { state: 'generating' }
  | { state: 'failed'; error: string }
  /** Legacy restored card whose record couldn't be located anymore. */
  | { state: 'unavailable' }
  | {
      state: 'complete';
      file: string;
      thumbUrl: string | null;
      /** ISO auto-delete instant (temporary storage); undefined once kept. */
      expiresAt: string | undefined;
      kept: boolean;
    };

export default function ImageJobCard({ jobId, prompt, turnTs }: ImageJobCardProps) {
  const [phase, setPhase] = useState<Phase>({ state: 'generating' });
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  // Resolved lazily for legacy cards; needed for the thumbnail/download paths.
  const [resolvedJobId, setResolvedJobId] = useState<string | null>(jobId ?? null);
  const resolvedJobIdRef = useRef(resolvedJobId);
  // One-shot guard for poll-failure reporting. `pollOnce` runs from THREE call
  // sites — the mount effect, the 60s interval, and the visibility handler — so
  // it is emphatically not a one-shot, and reporting per call would flood.
  const pollFailedRef = useRef(false);
  /**
   * Consecutive polls that SUCCEEDED but found no record for this card's known
   * jobId, and a one-shot so the finding is reported once.
   *
   * This is the image failure the `.catch` above cannot see, and the likelier
   * one: `listImageJobs` aggregates records "newest first, capped", so a job
   * that falls off the cap — or whose record was never flipped to terminal —
   * comes back as a perfectly healthy 200 with the job simply absent.
   * `applyRecord(undefined)` then returns immediately and the card sits on
   * "Generating…" forever, with no error anywhere. The studio consumer is
   * already known to hide an AccessDenied behind a bare catch, which produces
   * exactly this shape from the server side.
   */
  const missingPollsRef = useRef(0);
  const unresolvedReportedRef = useRef(false);
  /** When this card started waiting — the basis for the stalled-job budget. */
  const startedAtRef = useRef(Date.now());
  /** Polls this card has completed — reported with an unresolved card, because
   *  "waited 30 min" and "waited 30 min across 2 polls" are different faults. */
  const pollsRef = useRef(0);
  resolvedJobIdRef.current = resolvedJobId;
  // Legacy cards restore with a placeholder title; the record's prompt is
  // better once the job resolves.
  const [title, setTitle] = useState(prompt);
  // Server-stamped ETA (from the polled record) + a 1s tick driving the
  // countdown. FluffyBunny-style: clamp, never stall — past the estimate the
  // bar sits full and the copy flips to "Finalizing…".
  const [eta, setEta] = useState<EtaWindow | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  /** Apply a freshly-polled record; resolves the thumbnail on completion. */
  const applyRecord = useCallback(
    (record: ImageJob | undefined) => {
      if (record === undefined || phaseRef.current.state !== 'generating') {
        return;
      }
      if (record.status === 'failed') {
        setPhase({
          state: 'failed',
          error: record.error ?? 'The image could not be generated.',
        });
        return;
      }
      if (record.status === 'complete') {
        const file = record.files?.[0];
        if (file === undefined) {
          setPhase({ state: 'failed', error: 'Completed, but no image file was delivered.' });
          return;
        }
        setPhase({
          state: 'complete',
          file,
          thumbUrl: null,
          expiresAt: record.expiresAt,
          kept: record.kept === true,
        });
        if (isExpired(record.expiresAt)) {
          return; // the object is gone — no thumbnail to fetch
        }
        // One-time thumbnail link; failure keeps the Download button usable, so
        // this genuinely degrades rather than breaks — but a degraded mode has
        // to SAY it degraded, or "no thumbnails today" reads as a quiet day.
        void getDownloadUrl(`studio/${record.jobId}/${file}`)
          .then((link) => {
            setPhase((prev) =>
              prev.state === 'complete' ? { ...prev, thumbUrl: link.url } : prev,
            );
          })
          .catch((err: unknown) => {
            recordClientFailure(
              'image-thumb-failed',
              failureKind(err),
            );
          });
        return;
      }
      // Still generating — capture/refresh the server ETA for the countdown.
      setEta(etaWindowOf(record));
    },
    [],
  );

  /**
   * Report a card that a SUCCESSFUL poll cannot resolve or settle.
   *
   * Two shapes, both ending in a spinner that never stops:
   *  - `missing`: the listing came back fine and this jobId was not in it.
   *    Given a whole poll interval of grace before it counts, so a record that
   *    simply has not been written yet is not reported.
   *  - `stalled`: the record IS there and still `generating` well past any
   *    plausible generation time, i.e. nothing ever flipped it to terminal.
   *
   * Once per card either way — this runs from three call sites on a 60s timer.
   */
  const reportIfUnresolved = useCallback(
    (record: ImageJob | undefined): void => {
      if (unresolvedReportedRef.current || phaseRef.current.state !== 'generating') {
        return;
      }
      // The digest line is ALL an operator gets, so it has to carry enough to
      // act on. On 2026-09-24 this reported `image-job-unresolved — stalled ·
      // Chrome 153 · Android` and nothing else: not which job, not how long,
      // not what the record last said — so there was no way to tell a dead
      // studio worker from a record that never flipped, without guessing.
      const waitedS = Math.round((Date.now() - startedAtRef.current) / 1000);
      const where = (shape: string, extra?: string): string => {
        const base =
          `${shape} job=${jobId} waited=${String(waitedS)}s ` +
          `polls=${String(pollsRef.current)}`;
        return extra === undefined ? base : `${base} ${extra}`;
      };

      if (record === undefined) {
        missingPollsRef.current += 1;
        if (missingPollsRef.current >= MISSING_POLLS_BEFORE_REPORT) {
          unresolvedReportedRef.current = true;
          recordClientFailure('image-job-unresolved', where('missing'));
        }
        return;
      }
      missingPollsRef.current = 0;
      if (Date.now() - startedAtRef.current > STUCK_AFTER_MS) {
        unresolvedReportedRef.current = true;
        // `last=` is the discriminator: a record still `generating` after half
        // an hour means nothing flipped it (worker or completion consumer),
        // whereas any other status means the flip happened and the CARD is what
        // failed to settle.
        recordClientFailure(
          'image-job-unresolved',
          where('stalled', `last=${record.status}`),
        );
      }
    },
    [jobId],
  );

  /** One poll: read the records, resolve + apply this card's. Transient
   *  failures are silent — the next tick retries. */
  const pollOnce = useCallback(() => {
    pollsRef.current += 1;
    void listImageJobs()
      .then((jobs) => {
        const knownId = resolvedJobIdRef.current;
        if (knownId !== null) {
          const record = jobs.find((j) => j.jobId === knownId);
          reportIfUnresolved(record);
          applyRecord(record);
          return;
        }
        // Legacy resolution — one shot: the listing is capped, so a miss now
        // won't become a hit later; show the quiet unavailable state instead
        // of polling forever.
        const match = turnTs !== undefined ? nearestRecord(jobs, turnTs) : undefined;
        if (match === undefined) {
          setPhase({ state: 'unavailable' });
          return;
        }
        setResolvedJobId(match.jobId);
        if (match.prompt !== undefined && match.prompt.trim().length > 0) {
          setTitle(match.prompt);
        }
        applyRecord(match);
      })
      .then(() => {
        // A completed poll re-arms the guard so a later episode is reported.
        pollFailedRef.current = false;
      })
      .catch((err: unknown) => {
        // A failing status poll leaves the card on "Generating…" forever with
        // no error anywhere — the image-generation half of the reported "I
        // never see the result". Report once per failure episode.
        if (!pollFailedRef.current) {
          pollFailedRef.current = true;
          recordClientFailure(
            'image-status-failed',
            failureKind(err),
          );
        }
      });
  }, [turnTs, applyRecord, reportIfUnresolved]);

  // Immediate status on mount: a restored card for a finished job flips to
  // its thumbnail right away instead of waiting out the first minute tick.
  // (Hidden tabs skip it — the visibility catch-up below covers them.)
  useEffect(() => {
    if (phaseRef.current.state === 'generating' && document.visibilityState !== 'hidden') {
      pollOnce();
    }
  }, [pollOnce]);

  // 1s countdown tick — only while generating with a known ETA. Cheap enough
  // to keep running in the "Finalizing…" tail (a state set per second).
  useEffect(() => {
    if (phase.state !== 'generating' || eta === null) {
      return;
    }
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [phase.state, eta]);

  // Minute poll while non-terminal; skipped entirely when the tab is hidden.
  useEffect(() => {
    if (phase.state !== 'generating') {
      return;
    }
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        pollOnce();
      }
    }, IMAGE_CARD_POLL_MS);
    return () => clearInterval(timer);
  }, [phase.state, pollOnce]);

  // Catch-up poll when the tab returns to visible: hidden-tab ticks are
  // skipped (and throttled by the browser anyway), so without this the card
  // can sit on "Generating…" long after delivery.
  useEffect(() => {
    if (phase.state !== 'generating') {
      return;
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pollOnce();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [phase.state, pollOnce]);

  const download = useCallback(() => {
    const id = resolvedJobIdRef.current;
    if (phaseRef.current.state !== 'complete' || id === null) {
      return;
    }
    const file = phaseRef.current.file;
    // Fresh link per click — presigned URLs expire, a stored one could be dead.
    void getDownloadUrl(`studio/${id}/${file}`)
      .then((link) => {
        window.open(link.url, '_blank', 'noopener,noreferrer');
      })
      .catch((err: unknown) => {
        // User-initiated and one-per-click, so no guard is needed — but a
        // Download button that does nothing at all must not be invisible to us.
        recordClientFailure(
          'image-download-failed',
          failureKind(err),
        );
      });
  }, []);

  return (
    <div className="om-imgjob" data-state={phase.state}>
      <div className="om-imgjob__head">
        <span className="om-imgjob__icon" aria-hidden="true">
          ▣
        </span>
        <span className="om-imgjob__prompt" title={title}>
          {title}
        </span>
      </div>

      {phase.state === 'generating' && eta === null ? (
        <div className="om-imgjob__status">
          <span className="om-imgjob__pulse" aria-hidden="true" />
          Generating your image — it&rsquo;ll appear here when ready.
        </div>
      ) : null}

      {phase.state === 'generating' && eta !== null ? (
        (() => {
          const remainingSec = Math.max(0, (eta.readyAtMs - nowMs) / 1000);
          // Clamp — the bar never regresses and never stalls short of full.
          const progress = Math.min(1, Math.max(0, 1 - (eta.readyAtMs - nowMs) / eta.totalMs));
          return (
            <div className="om-imgjob__status om-imgjob__status--eta">
              <div className="om-imgjob__etarow">
                <span className="om-imgjob__pulse" aria-hidden="true" />
                <span>
                  {remainingSec > 0 ? 'Generating your image' : 'Finalizing — almost done…'}
                </span>
                {remainingSec > 0 ? (
                  <span className="om-imgjob__etatime">~{formatRemaining(remainingSec)}</span>
                ) : null}
              </div>
              <div
                className="om-imgjob__bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress * 100)}
              >
                <div className="om-imgjob__barfill" style={{ width: `${String(progress * 100)}%` }} />
              </div>
            </div>
          );
        })()
      ) : null}

      {phase.state === 'failed' ? (
        <div className="om-imgjob__status om-imgjob__status--failed" role="alert">
          {phase.error}
        </div>
      ) : null}

      {phase.state === 'unavailable' ? (
        <div className="om-imgjob__status">
          This image isn&rsquo;t available anymore — ask me to generate it again.
        </div>
      ) : null}

      {phase.state === 'complete' && isExpired(phase.expiresAt) ? (
        <div className="om-imgjob__status om-imgjob__status--failed" role="alert">
          This image expired (temporary storage) — ask me to generate it again.
        </div>
      ) : null}

      {phase.state === 'complete' && !isExpired(phase.expiresAt) ? (
        <div className="om-imgjob__done">
          {phase.thumbUrl !== null ? (
            <button
              type="button"
              className="om-imgjob__thumbbtn"
              title="Open the full image"
              onClick={download}
            >
              <img className="om-imgjob__thumb" src={phase.thumbUrl} alt={title} />
            </button>
          ) : null}
          <button type="button" className="om-imgjob__download" onClick={download}>
            Download
          </button>
          <span className="om-imgjob__expiry">
            {phase.kept
              ? 'Saved permanently.'
              : expiryLine(phase.expiresAt)}
          </span>
        </div>
      ) : null}
    </div>
  );
}
